import type {
	CacheConfig,
	Variables,
	RequestParameters,
	GraphQLResponse,
	FetchFunction,
} from 'relay-runtime';
import { Network, Observable } from 'relay-runtime';

import {
	setNetworkStatus,
	ClientNetworkStatus,
} from '@confluence/legacy-schema-bridge/entry-points/useNetworkStatusListenerCache';

import { BatchFetcher, DEFAULT_BATCHING_CONFIG } from './batchFetcher';
import { shouldTerminateQueriesOnExternalPage } from './externalShare';
import { readDataFromStream, writeDataToStream, initializeSSR } from './streaming';
import { getRelayWebSocketSubscriptionHandler } from './RelayWebSocketSubscriptionHandlerFactory';
import { composeMiddlewares } from './middlewareComposer';
import { getMiddlewareConfig } from './middlewareConfig';

const fetchFn: FetchFunction = (request, variables, cacheConfig) => {
	const batchFetcher = new BatchFetcher(DEFAULT_BATCHING_CONFIG);

	const result = readDataFromStream(request, variables);
	if (result) {
		return Observable.create((observer) => {
			observer.next({ data: result });
			observer.complete();
		});
	}
	if (
		shouldTerminateQueriesOnExternalPage({
			operationName: request.name,
			metadata: cacheConfig.metadata,
		})
	) {
		return Observable.create((observer) => {
			observer.complete();
		});
	}

	const promise = batchFetcher.fetch(request, variables, cacheConfig);

	return (
		writeDataToStream(request, variables, cacheConfig, promise) ||
		Observable.create((observer) => {
			promise
				.then((result: any) => {
					observer.next(result);
					observer.complete();
				})
				.catch((error: any) => {
					observer.error(error);
				});
		})
	);
};

const SSR_NAMESPACE = 'cgraphql';

const subscribeFn = (
	request: RequestParameters,
	variables: Variables,
	cacheConfig: CacheConfig,
): Observable<GraphQLResponse> => {
	const wsHandler = getRelayWebSocketSubscriptionHandler();

	return Observable.create((observer: any) => {
		const unsubscribe = wsHandler.subscribe(request, variables, cacheConfig, {
			next: (value: any) => observer.next(value),
			error: (error: Error) => observer.error(error),
			complete: () => observer.complete(),
		});

		return () => {
			unsubscribe();
		};
	});
};

export const getNetwork = () => {
	initializeSSR(SSR_NAMESPACE);
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = !!process.env.REACT_SSR;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSRTest = process.env.CLOUD_ENV === 'staging' || process.env.CLOUD_ENV === 'branch';

	// Get middleware configurations
	const middlewareConfig = getMiddlewareConfig({ isSSR, isSSRTest });

	// Compose all enabled middlewares
	const composedFetch = composeMiddlewares(fetchFn, middlewareConfig);

	const network = Network.create(composedFetch, subscribeFn);

	if (!isSSR) {
		setNetworkStatus(ClientNetworkStatus.ONLINE);
	}

	return network;
};

export const getNoopNetwork = () => {
	return Network.create((_request, _variables, _cacheConfig) => {
		return Observable.create((observer) => {
			observer.complete();
		});
	});
};
